import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PipelineLoan, PipelineStatus, STATUS_CONFIG, derivePipelineStatus, getBorrowerName, getLoanOfficerName, getMilestoneDoneDate } from '../types';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';

interface PipelineDataTableProps {
  loans: PipelineLoan[];
  isLoading: boolean;
}

type SortField = 'lock_date' | 'lock_expires' | 'loan_number' | 'borrower' | 'lo' | 'amount' | 'investor' | 'rate' | 'status' | 'status_date';
type SortDirection = 'asc' | 'desc';

export function PipelineDataTable({ loans, isLoading }: PipelineDataTableProps) {
  const [sortField, setSortField] = useState<SortField>('lock_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedLoans = useMemo(() => {
    return [...loans].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'lock_date':
          const dateA = a.rate_lock_buy_side_lock_date || '';
          const dateB = b.rate_lock_buy_side_lock_date || '';
          comparison = dateA.localeCompare(dateB);
          break;
        case 'loan_number':
          comparison = (a.trans_details_loan_number || '').localeCompare(b.trans_details_loan_number || '');
          break;
        case 'borrower':
          comparison = getBorrowerName(a).localeCompare(getBorrowerName(b));
          break;
        case 'lo':
          comparison = getLoanOfficerName(a).localeCompare(getLoanOfficerName(b));
          break;
        case 'amount':
          comparison = (a.trans_details_loan_amt || 0) - (b.trans_details_loan_amt || 0);
          break;
        case 'rate':
          comparison = (a.trans_details_interest_rate || 0) - (b.trans_details_interest_rate || 0);
          break;
        case 'investor':
          comparison = (a.investor_name || '').localeCompare(b.investor_name || '');
          break;
        case 'lock_expires':
          const expiresA = a.rate_lock_buy_side_lock_expires || '';
          const expiresB = b.rate_lock_buy_side_lock_expires || '';
          comparison = expiresA.localeCompare(expiresB);
          break;
        case 'status':
          comparison = derivePipelineStatus(a).localeCompare(derivePipelineStatus(b));
          break;
        case 'status_date':
          const statusDateA = getMilestoneDoneDate(a, derivePipelineStatus(a)) || '';
          const statusDateB = getMilestoneDoneDate(b, derivePipelineStatus(b)) || '';
          comparison = statusDateA.localeCompare(statusDateB);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [loans, sortField, sortDirection]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) {
return '-';
}
    try {
      return format(parseISO(dateStr), 'MM/dd/yy');
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) {
return '-';
}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatRate = (rate: number | null) => {
    if (rate === null || rate === undefined) {
return '-';
}
    return `${rate.toFixed(3)}%`;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  const SortableHeader = ({
    field,
    children
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => handleSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </Button>
  );

  const getStatusBadge = (status: PipelineStatus) => {
    const config = STATUS_CONFIG[status];
    return (
      <Badge variant="secondary" className={cn('text-xs', config.color)}>
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (loans.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No loans found for the selected criteria.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortableHeader field="lock_date">Lock Date</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="loan_number">Loan #</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="borrower">Borrower</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="lo">LO</SortableHeader>
            </TableHead>
            <TableHead className="text-right">
              <SortableHeader field="amount">Amount</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="investor">Investor</SortableHeader>
            </TableHead>
            <TableHead>Commitment #</TableHead>
            <TableHead className="text-right">
              <SortableHeader field="rate">Rate</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="lock_expires">Lock Expires</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="status">Status</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="status_date">Status Date</SortableHeader>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedLoans.map((loan) => {
            const status = derivePipelineStatus(loan);
            return (
              <TableRow key={loan.encompass_loan_id}>
                <TableCell className="font-medium">
                  {formatDate(loan.rate_lock_buy_side_lock_date)}
                </TableCell>
                <TableCell>{loan.trans_details_loan_number || '-'}</TableCell>
                <TableCell className="max-w-[150px] truncate">
                  {getBorrowerName(loan)}
                </TableCell>
                <TableCell className="max-w-[120px] truncate">
                  {getLoanOfficerName(loan)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(loan.trans_details_loan_amt)}
                </TableCell>
                <TableCell className="max-w-[150px] truncate">
                  {loan.investor_name || '-'}
                </TableCell>
                <TableCell className="max-w-[100px] truncate">
                  {loan.rate_lock_investor_commitment || '-'}
                </TableCell>
                <TableCell className="text-right">
                  {formatRate(loan.trans_details_interest_rate)}
                </TableCell>
                <TableCell>
                  {formatDate(loan.rate_lock_buy_side_lock_expires)}
                </TableCell>
                <TableCell>{getStatusBadge(status)}</TableCell>
                <TableCell>{formatDate(getMilestoneDoneDate(loan, status))}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
